import React, { useState, useRef, useEffect } from 'react';
import { Tag, Input, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const CustomTagInput = ({ value = [], onChange, disabled }) => {
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [editInputIndex, setEditInputIndex] = useState(-1);
  const [editInputValue, setEditInputValue] = useState('');
  const inputRef = useRef(null);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (inputVisible) inputRef.current?.focus();
  }, [inputVisible]);

  useEffect(() => {
    editInputRef.current?.focus();
  }, [editInputValue]);

  const handleClose = (removedTag) => {
    if (disabled) return;
    const newTags = value.filter((tag) => tag !== removedTag);
    onChange(newTags);
  };

  const showInput = () => {
    if (disabled) return;
    setInputVisible(true);
  };

  const sanitizeInput = (input) => {
    return input
      .replace(/[\s,]/g, '')
      .replace(/[^a-zA-Z0-9_]/g, '');
  };

  const handleInputChange = (e) => {
    let newValue = e.target.value;
    if (newValue.length === 1 && /[\s,]/.test(newValue)) {
      return;
    }
    setInputValue(newValue);
  };

  const handleInputConfirm = () => {
    const sanitizedValue = sanitizeInput(inputValue.trim());
    if (sanitizedValue && !value.includes(sanitizedValue) && !disabled) {
      onChange([...value, sanitizedValue]);
    }
    setInputVisible(false);
    setInputValue('');
  };

  const handleKeyDown = (e) => {
    if (e.key === ' ' || e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      handleInputConfirm();
    }
  };

  const handleEditInputChange = (e) => {
    setEditInputValue(e.target.value);
  };

  const handleEditInputConfirm = () => {
    if (disabled) return;
    const sanitizedValue = sanitizeInput(editInputValue.trim());
    if (sanitizedValue && !value.includes(sanitizedValue)) {
      const newTags = [...value];
      newTags[editInputIndex] = sanitizedValue;
      onChange(newTags);
    }
    setEditInputIndex(-1);
    setEditInputValue('');
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const sanitizedText = sanitizeInput(pastedText);
    if (sanitizedText) {
      setInputValue(sanitizedText);
    }
  };

  return (
    <div>
      {value.map((tag, index) => {
        if (editInputIndex === index) {
          return (
            <Input
              ref={editInputRef}
              key={tag}
              size='small'
              className='mr-8 w-auto'
              value={editInputValue}
              onChange={handleEditInputChange}
              onBlur={handleEditInputConfirm}
              onPressEnter={handleEditInputConfirm}
              disabled={disabled}
            />
          );
        }

        const isLongTag = tag.length > 20;
        const tagElem = (
          <Tag
            key={tag}
            closable={!disabled}
            onClose={() => handleClose(tag)}
            className='tag-selected border-none bg-white p-0 mt-8 mb-8'
          >
            <span
              onDoubleClick={(e) => {
                if (!disabled) {
                  setEditInputIndex(index);
                  setEditInputValue(tag);
                  e.preventDefault();
                }
              }}
              className='manrope-14-medium'
            >
              {isLongTag ? `${tag.slice(0, 20)}...` : tag}
            </span>
          </Tag>
        );

        return isLongTag ? (
          <Tooltip title={tag} key={tag}>
            {tagElem}
          </Tooltip>
        ) : (
          tagElem
        );
      })}

      {inputVisible ? (
        <Input
          ref={inputRef}
          type='text'
          size='small'
          className='mr-8 w-auto'
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputConfirm}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={disabled}
        />
      ) : (
        <Tag
          onClick={showInput}
          className={`w-auto border-none tag-input ${
            disabled ? 'cursor-not-allowed tag-input-disabled' : 'cursor-pointer'
          }`}
        >
          <PlusOutlined className='tag-icon' /> <span className='tag-placeholder'>New Tag</span>
        </Tag>
      )}
    </div>
  );
};

export default CustomTagInput;
